import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

interface Props {
  value: number;                      // valor em centavos
  onChange: (cents: number) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
}

// Converte centavos para string de exibição: 123456 → "1234,56"
function centsToDisplay(cents: number): string {
  if (cents === 0) return '';
  return (cents / 100).toFixed(2).replace('.', ',');
}

// Converte string digitada para centavos.
// Aceita: "1234,56" | "1234.56" | "1234" | "1.234,56" (formato BR com milhar)
function parseToCents(raw: string): number {
  if (!raw.trim()) return 0;

  // Remove tudo que não é dígito, vírgula ou ponto
  const cleaned = raw.replace(/[^\d.,]/g, '');
  if (!cleaned) return 0;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot   = cleaned.lastIndexOf('.');

  // Se não há separador → número inteiro → multiplica por 100
  if (lastComma === -1 && lastDot === -1) {
    return parseInt(cleaned, 10) * 100;
  }

  const lastSep    = Math.max(lastComma, lastDot);
  const afterSep   = cleaned.slice(lastSep + 1);

  // Se depois do separador há mais de 2 dígitos, é separador de milhar (ex.: "1.234")
  if (afterSep.length > 2) {
    const intOnly = cleaned.replace(/[.,]/g, '');
    return parseInt(intOnly || '0', 10) * 100;
  }

  // Separador decimal
  const intPart = cleaned.slice(0, lastSep).replace(/[.,]/g, '');
  const decPart = afterSep.padEnd(2, '0').slice(0, 2);
  return parseInt(intPart || '0', 10) * 100 + parseInt(decPart, 10);
}

export function CurrencyInput({ value, onChange, className, placeholder, disabled, id }: Props) {
  const [text, setText]       = useState(() => centsToDisplay(value));
  const [focused, setFocused] = useState(false);
  const internalRef           = useRef(false); // evita loop: onChange → value → useEffect

  // Sincroniza valor externo quando não está em foco
  useEffect(() => {
    if (!focused && !internalRef.current) {
      setText(centsToDisplay(value));
    }
    internalRef.current = false;
  }, [value, focused]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    // Permite apenas dígitos, vírgula e ponto
    const raw = e.target.value.replace(/[^\d.,]/g, '');
    setText(raw);
    const cents = parseToCents(raw);
    internalRef.current = true;
    onChange(cents);
  }

  function handleFocus() {
    setFocused(true);
    // Mostra o valor limpo (sem formatação extra) ao focar
    if (value === 0) setText('');
  }

  function handleBlur() {
    setFocused(false);
    // Reformata ao sair do campo
    const cents = parseToCents(text);
    internalRef.current = true;
    onChange(cents);
    setText(centsToDisplay(cents));
  }

  return (
    <input
      id={id}
      type="text"
      inputMode="decimal"
      disabled={disabled}
      placeholder={placeholder ?? '0,00'}
      className={cn('tabular-nums', className)}
      value={text}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
}
